import * as React from 'react';
import { styled } from '@mui/material/styles';
import Stack from '@mui/material/Stack';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import StepButton from '@mui/material/StepButton';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Check from '@mui/icons-material/Check';
import StepConnector, { stepConnectorClasses } from '@mui/material/StepConnector';
import { StepIconProps } from '@mui/material/StepIcon';
import Typography from '@mui/material/Typography';

// ************** Types and Interfaces **************
export type StepperVariant = 'qonto';

export type StepIconStyle = 'dot' | 'numbered';

export type QontoStepperColors = {
    active?: string;
    completed?: string;
    inactive?: string;
};

export type StepAlertSeverity = 'error' | 'warning' | 'info';

export type StepAlert = {
    message: string;
    severity?: StepAlertSeverity;
};

export type StepperProps = {
    steps: string[];
    activeStep?: number;
    variant?: StepperVariant;
    stepIconStyle?: StepIconStyle;
    linear?: boolean;
    qontoColors?: QontoStepperColors;
    stepAlerts?: Record<number, StepAlert | string>;
    children?: React.ReactNode[];
    withControl?: boolean;
    onStepChange?: (step: number) => void;
};

const DEFAULT_QONTO_COLORS: Required<QontoStepperColors> = {
    active: "#0288d1",
    completed: "#0288d1",
    inactive: "#c7cedb"
};

// ******************* Custom Stepper Components ******************
const QontoConnector = styled(StepConnector)(({ theme }) => ({
    [`&.${stepConnectorClasses.alternativeLabel}`]: {
        top: 10,
        left: 'calc(-50% + 16px)',
        right: 'calc(50% + 16px)',
    },
    [`&.${stepConnectorClasses.active}`]: {
        [`& .${stepConnectorClasses.line}`]: {
            borderColor: `var(--hi-stepper-qonto-active-color, ${DEFAULT_QONTO_COLORS.active})`,
        },
    },
    [`&.${stepConnectorClasses.completed}`]: {
        [`& .${stepConnectorClasses.line}`]: {
            borderColor: `var(--hi-stepper-qonto-completed-color, ${DEFAULT_QONTO_COLORS.completed})`,
        },
    },
    [`& .${stepConnectorClasses.line}`]: {
        borderColor: `var(--hi-stepper-qonto-inactive-color, ${theme.palette.mode === 'dark' ? theme.palette.grey[800] : DEFAULT_QONTO_COLORS.inactive
            })`,
        borderTopWidth: 3,
        borderRadius: 1,
    },
}));

const QontoStepIconRoot = styled('div')<{ ownerState: { active?: boolean } }>(
    ({ theme }) => ({
        color: `var(--hi-stepper-qonto-inactive-color, ${theme.palette.mode === 'dark' ? theme.palette.grey[700] : DEFAULT_QONTO_COLORS.inactive
            })`,
        display: 'flex',
        height: 22,
        alignItems: 'center',
        '& .QontoStepIcon-completedIcon': {
            color: `var(--hi-stepper-qonto-completed-color, ${DEFAULT_QONTO_COLORS.completed})`,
            zIndex: 1,
            fontSize: 18,
        },
        '& .QontoStepIcon-circle': {
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: 'currentColor',
        },
        variants: [
            {
                props: ({ ownerState }) => ownerState.active,
                style: {
                    color: `var(--hi-stepper-qonto-active-color, ${DEFAULT_QONTO_COLORS.active})`,
                },
            },
        ],
    }),
);

/**
 * Custom step icon for the Qonto variant of the stepper.
 * @param props StepIconProps provided by MUI Stepper
 * @returns A styled step icon component
 */
function QontoStepIcon(props: StepIconProps) {
    const { active, completed, className } = props;

    return (
        <QontoStepIconRoot ownerState={{ active }} className={className}>
            {completed ? (
                <Check className="QontoStepIcon-completedIcon" />
            ) : (
                <div className="QontoStepIcon-circle" />
            )}
        </QontoStepIconRoot>
    );
}

/**
 * MUI Stepper Component
 * @param steps The labels for each step
 * @param activeStep The index of the currently active step
 * @param variant The visual style of the stepper (currently supports 'qonto')
 * @param qontoColors Optional colors for the 'qonto' variant (active, completed, inactive)
 * @param stepAlerts Optional alerts mapped by step index. { message, severity }
 * @returns A MUI Stepper component with the specified configuration
 */
export default function HIStepper({
    steps,
    activeStep: controlledActiveStep = 0,
    variant = 'qonto',
    stepIconStyle = 'dot',
    linear = true,
    qontoColors,
    stepAlerts,
    children,
    onStepChange,
    withControl = false,
}: StepperProps) {
    const hasContent = children && children.length > 0;
    const [internalStep, setInternalStep] = React.useState(controlledActiveStep);
    const [completed, setCompleted] = React.useState<Record<number, boolean>>({});
    // Counter used to force re-mount (reset) of uncompleted step content
    const [resetCounters, setResetCounters] = React.useState<Record<number, number>>({});

    // Sync internal step when controlled prop changes
    React.useEffect(() => {
        setInternalStep(controlledActiveStep);
    }, [controlledActiveStep]);

    const activeStep = hasContent ? internalStep : controlledActiveStep;

    const resolvedQontoColors = {
        ...DEFAULT_QONTO_COLORS,
        ...qontoColors,
    };

    const qontoColorVariables = {
        '--hi-stepper-qonto-active-color': resolvedQontoColors.active,
        '--hi-stepper-qonto-completed-color': resolvedQontoColors.completed,
        '--hi-stepper-qonto-inactive-color': resolvedQontoColors.inactive,
    } as React.CSSProperties;

    if (steps.length === 0) {
        return <Typography variant="body2">No steps provided.</Typography>;
    }

    // Reset uncompleted step content by incrementing its counter (forces re-mount)
    const resetStepIfNotCompleted = (step: number) => {
        if (!completed[step]) {
            setResetCounters((prev) => ({
                ...prev,
                [step]: (prev[step] ?? 0) + 1,
            }));
        }
    };

    const handleStep = (step: number) => () => {
        // Reset current step content if not completed
        resetStepIfNotCompleted(activeStep);
        setInternalStep(step);
        onStepChange?.(step);
    };

    const handleNext = () => {
        const next = Math.min(activeStep + 1, steps.length - 1);
        // Reset current step content if not completed
        if (!linear) {
            resetStepIfNotCompleted(activeStep);
        }
        if (linear) {
            setCompleted((prev) => ({ ...prev, [activeStep]: true }));
        }
        setInternalStep(next);
        onStepChange?.(next);
    };

    const handleBack = () => {
        const prev = Math.max(activeStep - 1, 0);
        // Reset current step content if not completed
        if (!linear) {
            resetStepIfNotCompleted(activeStep);
        }
        setInternalStep(prev);
        onStepChange?.(prev);
    };

    const handleCompleteStep = () => {
        setCompleted((prev) => ({ ...prev, [activeStep]: true }));
    };

    const allStepsCompleted = () => {
        return Object.keys(completed).length === steps.length;
    };

    const useCustomIcon = stepIconStyle === 'dot';
    const StepIconComponent = useCustomIcon ? QontoStepIcon : undefined;
    const connector = useCustomIcon ? <QontoConnector /> : undefined;

    const renderStepLabel = (label: string, index: number) => {
        const rawAlert = stepAlerts?.[index];
        const alert: StepAlert | undefined =
            typeof rawAlert === 'string'
                ? { message: rawAlert, severity: 'warning' }
                : rawAlert;
        const severity = alert?.severity ?? 'warning';
        const labelProps: {
            optional?: React.ReactNode;
            error?: boolean;
        } = {};

        if (alert?.message) {
            labelProps.optional = (
                <Typography
                    variant="caption"
                    sx={{
                        display: 'block',
                        mt: 0.5,
                        textAlign: 'center',
                        color: (theme) => {
                            if (severity === 'error') {
                                return theme.palette.mode === 'dark'
                                    ? '#ef9a9a'
                                    : '#d32f2f';
                            }

                            if (severity === 'warning') {
                                return theme.palette.mode === 'dark'
                                    ? '#ffcc80'
                                    : '#ed6c02';
                            }

                            return theme.palette.mode === 'dark'
                                ? '#90caf9'
                                : '#0288d1';
                        },
                        lineHeight: 1.3,
                    }}
                >
                    {alert.message}
                </Typography>
            );
            labelProps.error = severity === 'error';
        }

        return (
            <StepLabel
                StepIconComponent={StepIconComponent}
                {...labelProps}
            >
                {label}
            </StepLabel>
        );
    };

    return (
        <Stack sx={{ width: '100%' }} spacing={2}>
            {variant === 'qonto' && (
                <>
                    <Stepper
                        alternativeLabel
                        nonLinear={!linear}
                        activeStep={activeStep}
                        connector={connector}
                        sx={useCustomIcon ? qontoColorVariables : undefined}
                    >
                        {steps.map((label, index) => {
                            // In linear mode: steps before activeStep are completed automatically
                            // In non-linear mode: completed is tracked manually via the "Compléter" button
                            const isCompleted = linear
                                ? index < activeStep || completed[index] === true
                                : completed[index] === true;

                            return (
                                <Step
                                    key={`qonto-${label}`}
                                    completed={isCompleted}
                                    disabled={linear && hasContent && index > activeStep && !completed[index]}
                                >
                                    {!linear ? (
                                        <StepButton onClick={handleStep(index)}>
                                            {renderStepLabel(label, index)}
                                        </StepButton>
                                    ) : (
                                        renderStepLabel(label, index)
                                    )}
                                </Step>
                            );
                        })}
                    </Stepper>

                    {hasContent && (
                        <Box sx={{ mt: 2 }}>
                            {children.map((child, index) => (
                                <Box
                                    key={`step-content-${index}-${resetCounters[index] ?? 0}`}
                                    sx={{ display: index === activeStep ? 'block' : 'none', mb: 2 }}
                                >
                                    {child}
                                </Box>
                            ))}
                            {withControl && (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Button
                                        disabled={activeStep === 0}
                                        onClick={handleBack}
                                    >
                                        Précédent
                                    </Button>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        {!linear && (
                                            <Button
                                                variant="outlined"
                                                color={completed[activeStep] ? 'success' : 'primary'}
                                                onClick={handleCompleteStep}
                                            >
                                                {completed[activeStep] ? 'Étape complétée ✓' : 'Compléter l\'étape'}
                                            </Button>
                                        )}
                                        <Button
                                            variant="contained"
                                            disabled={activeStep === steps.length - 1 && (linear || allStepsCompleted())}
                                            onClick={handleNext}
                                        >
                                            Suivant
                                        </Button>
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    )}
                </>
            )}
        </Stack>
    );
}